import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import {
  Badge,
  Box,
  IconButton,
  Icons,
  Typography,
  useTheme,
  TextField,
} from '@ublend-npm/aulaui-core';
import Divider from '@mui/material/Divider';
import { useSelector } from 'react-redux';
import InfiniteScroller from '../../../../containers/common/InfiniteScroller';
import OutlookEmailRow from './components/OutlookEmailRow';
import {
  OutlookAllEmailsSkeleton,
  OutlookAllEmailsRowSkeleton,
} from './components/OutlookAllEmailsSkeleton';
import { OutlookMessage } from './types';
import { CircularProgress } from '@material-ui/core';
import { getHomeViewRole } from '@app/selectors/home';
import { trackOutlookSearched } from './outlookAnalytics';

type OutlookAllEmailsDialogProps = {
  open: boolean;
  messages: OutlookMessage[];
  isLoading?: boolean;
  unreadCount?: number;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  onClose: () => void;
  /** Called with the email's own `id` (not conversationId). */
  onMessageClick: (emailId: string, itemIndex: number) => void;
  /** Called with the search query; resolves with matching emails and nextLink. */
  onSearch: (
    query?: string,
    nextLink?: string
  ) => Promise<{ emails: OutlookMessage[]; nextLink?: string }>;
};

const OutlookAllEmailsDialog = ({
  open,
  messages,
  isLoading = false,
  unreadCount,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  onClose,
  onMessageClick,
  onSearch,
}: OutlookAllEmailsDialogProps) => {
  const theme = useTheme();
  const homeViewRole = useSelector(getHomeViewRole);
  const searchTextSecondary = theme.palette.text.secondary;

  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<OutlookMessage[] | null>(
    null
  );
  const [searchNextLink, setSearchNextLink] = useState<string | null>(null);
  const [isLoadingMoreSearch, setIsLoadingMoreSearch] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(
    null
  );

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    setSearchResults(null);
    setSearchNextLink(null);
    setIsSearching(false);
    setIsLoadingMoreSearch(false);
  }, []);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      handleClearSearch();
      setSelectedMessageId(null);
    }
  }, [open, handleClearSearch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (!value.trim()) {
      setSearchResults(null);
      setSearchNextLink(null);
      setIsSearching(false);
      setIsLoadingMoreSearch(false);
    }
  };

  const handleSearch = async () => {
    const trimmed = searchTerm.trim();
    if (!trimmed) {
      handleClearSearch();
      return;
    }
    trackOutlookSearched(homeViewRole);
    setIsSearching(true);
    setSearchResults(null);
    try {
      const result = await onSearch(trimmed);
      setSearchResults(result.emails);
      setSearchNextLink(result.nextLink ?? null);
    } catch {
      setSearchResults([]);
      setSearchNextLink(null);
    } finally {
      setIsSearching(false);
    }
  };

  // Load next page of search results
  const loadMoreSearch = useCallback(async () => {
    if (!searchNextLink || isLoadingMoreSearch) return;
    setIsLoadingMoreSearch(true);
    try {
      const result = await onSearch(undefined, searchNextLink);
      setSearchResults((prev) => [...(prev ?? []), ...result.emails]);
      setSearchNextLink(result.nextLink ?? null);
    } catch {
      // Keep existing results
    } finally {
      setIsLoadingMoreSearch(false);
    }
  }, [searchNextLink, isLoadingMoreSearch, onSearch]);

  const isSearchMode = searchResults !== null || isSearching;
  const activeItems = isSearchMode ? searchResults ?? [] : messages;
  const activeHasMore = isSearchMode ? Boolean(searchNextLink) : hasMore;
  const activeLoading = isSearchMode ? isLoadingMoreSearch : isLoadingMore;
  const handleLoadMore = isSearchMode
    ? loadMoreSearch
    : onLoadMore ?? (() => {});

  const handleSelect = (message: OutlookMessage, itemIndex: number) => {
    setSelectedMessageId(message.id);
    onMessageClick(message.id, itemIndex);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      aria-labelledby="outlook-all-emails-title"
      PaperProps={{
        sx: {
          maxWidth: '648px',
          width: { xs: '100%', sm: 'calc(100% - 64px)' },
          height: { xs: '100%', sm: '85vh' },
          m: { xs: 0, sm: 4 },
          borderRadius: { xs: 0, sm: 2 },
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        id="outlook-all-emails-title"
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box display="flex" alignItems="center">
          <Typography
            variant="subtitle1"
            component="h2"
            color="primary"
            sx={{
              fontSize: { xs: '15px', sm: '16px' },
              lineHeight: '24px',
              fontWeight: 500,
              fontFamily: 'Roboto, sans-serif',
            }}
          >
            Inbox
          </Typography>

          {unreadCount !== undefined && unreadCount > 0 ? (
            <Badge
              badgeContent={unreadCount}
              sx={{
                ml: 2.5,
                '& .MuiBadge-badge': {
                  backgroundColor: '#e8dcfcff',
                  color: theme.palette.getContrastText('#e8dcfcff'),
                },
              }}
            />
          ) : null}
        </Box>

        <IconButton
          aria-label="Close all emails"
          onClick={onClose}
          size="small"
          sx={{
            width: 24,
            height: 24,
            p: '5px',
            color: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.56)'
                : 'rgba(35, 31, 41, 0.56)',
            '&:hover': {
              bgcolor: 'transparent',
            },
          }}
        >
          <Icons.Close sx={{ fontSize: 24 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 2,
          pt: 0,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          height: '100%',
          overflow: 'hidden',
          bgcolor: 'transparent',
          backgroundImage: 'none',
        }}
      >
        {/* Search bar */}
        <TextField
          label="Search emails"
          placeholder="Search"
          value={searchTerm}
          onChange={handleSearchChange}
          leftElement={
            isSearching ? (
              <CircularProgress
                style={{ color: searchTextSecondary }}
                size={20}
              />
            ) : (
              <Icons.Search
                sx={{ color: searchTextSecondary }}
                fontSize="small"
              />
            )
          }
          rightElement={
            searchTerm !== '' ? (
              <Icons.Close
                sx={{ color: searchTextSecondary, cursor: 'pointer' }}
                fontSize="medium"
                onClick={handleClearSearch}
              />
            ) : undefined
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSearch();
            }
          }}
        />

        {/* Initial loading or search loading skeleton */}
        {((isSearching && searchResults === null) ||
          (isLoading && !isSearchMode && activeItems.length === 0)) && (
          <OutlookAllEmailsSkeleton count={6} />
        )}

        {/* No results */}
        {!isSearching && isSearchMode && searchResults?.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            py={3}
          >
            No emails found for &ldquo;{searchTerm.trim()}&rdquo;
          </Typography>
        )}

        {/* Virtualized Infinite Scroller */}
        {(!isSearching || searchResults !== null) &&
          (!isLoading || isSearchMode || activeItems.length > 0) &&
          (!isSearchMode || (searchResults && searchResults.length > 0)) && (
            <Box sx={{ flex: 1, minHeight: 0, height: '100%' }}>
              <InfiniteScroller
                items={activeItems}
                loading={activeLoading}
                hasMore={activeHasMore}
                loadMore={handleLoadMore}
                height="100%"
                ariaLabel="All emails list"
                footer={
                  activeLoading ? (
                    <Box py={1}>
                      <OutlookAllEmailsRowSkeleton compact />
                    </Box>
                  ) : null
                }
                renderItem={(message, index) => (
                  <Box key={message.id}>
                    <OutlookEmailRow
                      message={message}
                      isSelected={selectedMessageId === message.id}
                      compact
                      onClick={(selected) => handleSelect(selected, index)}
                    />
                    {index < activeItems.length - 1 && (
                      <Divider sx={{ bgcolor: theme.palette.divider, my: 1 }} />
                    )}
                  </Box>
                )}
              />
            </Box>
          )}
      </DialogContent>
    </Dialog>
  );
};

export default OutlookAllEmailsDialog;
